import React, { useMemo } from "react";
import { Box, Grid, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import PersonRemoveAlt1Icon from "@mui/icons-material/PersonRemoveAlt1";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import useDownloadAssignmentsStatusReport from "../hooks/useDownloadAssignmentsStatusReport";

const calculatePercentage = (value, total) => {
  if (!total) return 0;

  return (value / total) * 100;
};

const StatusCard = ({ icon: Icon, label, value, color, backgroundColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.25,
      minHeight: 82,
      height: "100%",
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "#ffffff",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 34,
          height: 34,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          bgcolor: backgroundColor,
          color,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.2,
            fontSize: "0.82rem",
          }}
        >
          {label}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            mt: 0.25,
            fontWeight: 700,
            fontSize: "1.15rem",
            lineHeight: 1.1,
            color,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const ApplicationAssignmentsSummary = ({ summary, appId }) => {
  const normalizedSummary = useMemo(
    () => ({
      total: Number(summary?.total || 0),
      pending: Number(summary?.pending || 0),
      started: Number(summary?.started || 0),
      finished: Number(summary?.finished || 0),
      not_evaluable: Number(summary?.not_evaluable || 0),
      withdrawn: Number(summary?.withdrawn || 0),
    }),
    [summary],
  );

  const evaluableTotal = normalizedSummary.pending + normalizedSummary.started + normalizedSummary.finished;

  const finishedPercentage = calculatePercentage(normalizedSummary.finished, evaluableTotal);

  const roundedFinishedPercentage = Number(finishedPercentage.toFixed(2));

  const formattedFinishedPercentage = Number.isInteger(roundedFinishedPercentage)
    ? roundedFinishedPercentage.toLocaleString("es-CO")
    : roundedFinishedPercentage.toLocaleString("es-CO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

  const statusCards = [
    {
      key: "total",
      label: "Cuestionarios asignados",
      value: normalizedSummary.total,
      icon: AssignmentIcon,
      color: "#333333",
      backgroundColor: "#F3F3F3",
    },
    {
      key: "pending",
      label: "Pendientes",
      value: normalizedSummary.pending,
      icon: PendingActionsIcon,
      color: "#4A4A4A",
      backgroundColor: "#F3F3F3",
    },
    {
      key: "started",
      label: "Iniciados",
      value: normalizedSummary.started,
      icon: PlayCircleOutlineIcon,
      color: "#003C75",
      backgroundColor: "#E8F0F8",
    },
    {
      key: "finished",
      label: "Terminados",
      value: normalizedSummary.finished,
      icon: CheckCircleOutlineIcon,
      color: "#737300",
      backgroundColor: "#F7F8D9",
    },
    {
      key: "not_evaluable",
      label: "No evaluables",
      value: normalizedSummary.not_evaluable,
      icon: BlockIcon,
      color: "#686868",
      backgroundColor: "#F1F1F1",
    },
    {
      key: "withdrawn",
      label: "Retirados",
      value: normalizedSummary.withdrawn,
      icon: PersonRemoveAlt1Icon,
      color: "#9A6700",
      backgroundColor: "#FFF4D8",
    },
  ];

  const { downloadAssignmentsStatusReport, loading: downloading, error: downloadError } = useDownloadAssignmentsStatusReport();

  const handleDownload = async () => {
    try {
      await downloadAssignmentsStatusReport(appId);
    } catch (e) {
      // El error ya se maneja dentro del hook useDownloadAssignmentsStatusReport
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: { xs: 2, md: 2.5 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "#ffffff",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Resumen general
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Estado general de los cuestionarios asignados en esta aplicación.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={downloading || !appId}
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
        >
          Descargar informe de estados
        </Button>
      </Stack>

      {downloadError && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 2 }}>
          {downloadError.message}
        </Typography>
      )}

      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          mt: 2.5,
          p: 2,
          borderRadius: 2,
          bgcolor: "#FAFAFA",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          sx={{ mb: 1.25 }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Avance de la aplicación
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {normalizedSummary.finished} de {evaluableTotal} cuestionarios evaluables terminados
              {normalizedSummary.not_evaluable > 0 || normalizedSummary.withdrawn > 0 ? (
                <Box component="span" sx={{ color: "text.disabled", ml: 0.5 }}>
                  ({normalizedSummary.not_evaluable} no evaluables, {normalizedSummary.withdrawn} retirados excluidos)
                </Box>
              ) : null}
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.1rem",
              color: "#737300",
            }}
          >
            {formattedFinishedPercentage}%
          </Typography>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={roundedFinishedPercentage}
          sx={{
            height: 9,
            borderRadius: 8,
            bgcolor: "#E8E8E8",
            "& .MuiLinearProgress-bar": {
              borderRadius: 8,
              bgcolor: "#FAEF5C",
            },
          }}
        />
      </Paper>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Distribución por estado
      </Typography>

      <Grid container spacing={1.5}>
        {statusCards.map((status) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={status.key}>
            <StatusCard icon={status.icon} label={status.label} value={status.value} color={status.color} backgroundColor={status.backgroundColor} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default ApplicationAssignmentsSummary;
